import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import { calculatePeriodsForYear } from '../utils/payroll-calculator';
import { Icons } from '../components/Icons';
import './PayrollPeriods.css';

function PayrollPeriods({ org }) {
  const [anchorDates, setAnchorDates] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedFrequency, setSelectedFrequency] = useState('biweekly');
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const [setupDate, setSetupDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  const triggerNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchAnchorDates = useCallback(async () => {
    if (!org?.id) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/organizations/${org.id}/payroll-anchors`, org.tenantId);
      setAnchorDates(data);
    } catch (error) {
      triggerNotification('Failed to load anchor configurations.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [org?.id]);

  useEffect(() => {
    fetchAnchorDates();
  }, [fetchAnchorDates]);

  const handleOneTimeSetup = async () => {
    if (!setupDate) {
      triggerNotification('Please select a valid start date.', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      await apiFetch(`/organizations/${org.id}/payroll-anchors`, org.tenantId, {
        method: 'POST',
        body: { pay_frequency: selectedFrequency, anchor_date: setupDate },
      });
      triggerNotification(`${selectedFrequency} configuration successfully saved.`);
      await fetchAnchorDates();
      setSetupDate('');
    } catch (error) {
      triggerNotification('Failed to save configuration network error.', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const currentAnchor = anchorDates[selectedFrequency];
  const displayPeriods = currentAnchor
    ? calculatePeriodsForYear(currentAnchor, viewYear, selectedFrequency)
    : [];

  const showSetupForm = !currentAnchor || setupDate;
  const yearOptions = [viewYear - 1, viewYear, viewYear + 1, viewYear + 2];

  if (!org) return null;

  return (
    <div className="op-periods container-fluid py-4 px-3 px-md-4" style={{ minHeight: '100vh' }}>

      {/* Toast Notification */}
      {notification && (
        <div className={`alert alert-${notification.type} alert-dismissible fade show position-fixed top-0 op-toast shadow-sm mt-3 mt-md-4`} role="alert" style={{ minWidth: '300px', zIndex: 1070 }}>
          <strong>{notification.type === 'success' ? 'Success!' : notification.type === 'warning' ? 'Attention:' : 'Error!'}</strong> {notification.message}
          <button type="button" className="btn-close" onClick={() => setNotification(null)} aria-label="Close"></button>
        </div>
      )}

      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Icons.Document />
            <h1 className="h4 op-page-title mb-0">Pay Schedules</h1>
          </div>
          <p className="op-subtitle small mb-0 mt-1">Dynamic lifecycle tracking. Initialize once, generate infinitely.</p>
        </div>

        {/* Year Selector — moved up next to the page title */}
        <div className="d-flex align-items-center op-year-picker px-3 py-2 rounded-3 shadow-sm flex-shrink-0">
          <label className="fw-semibold me-3 small text-uppercase tracking-wider mb-0">Fiscal Year</label>
          <select
            className="form-select form-select-sm fw-bold shadow-none op-year-select"
            value={viewYear}
            onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
            style={{ minWidth: '110px', backgroundColor: '#fff' }}
          >
            {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>
      </div>

      {/* Filter / Navigation Bar */}
      <div className="card op-card shadow-sm border-0 mb-4 rounded-3">
        <div className="card-body p-3 p-md-4">

          {/* Frequency Pills */}
          <ul className="nav nav-pills gap-2 mb-0 justify-content-center justify-content-lg-start" style={{ fontSize: '0.9rem' }}>
            {['weekly', 'biweekly', 'semi-monthly', 'monthly'].map((freq) => (
              <li className="nav-item" key={freq}>
                <button
                  className={`nav-link op-freq-pill rounded-pill text-capitalize px-4 py-2 fw-semibold ${
                    selectedFrequency === freq ? 'active shadow-sm' : ''
                  }`}
                  onClick={() => {
                    setSelectedFrequency(freq);
                    setSetupDate(''); // Reset setup date if they switch tabs while editing
                  }}
                  style={{ transition: 'all 0.2s ease-in-out' }}
                >
                  {freq === 'biweekly' ? 'Bi-Weekly' : freq}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="card op-card shadow-sm border-0 p-5 text-center op-subtitle rounded-3">
          <div className="spinner-border op-spinner-border mb-3" role="status"></div>
          <span>Loading configuration data...</span>
        </div>
      ) : showSetupForm ? (

        /* One-Time Setup Wizard */
        <div className="card op-card shadow-sm border-0 rounded-3 mt-4">
          <div className="card-body text-center p-4 p-md-5">
            <div className="max-w-md mx-auto py-4">
              <div className="op-setup-icon"><Icons.Settings /></div>
              <h4 className="fw-bold mb-3" style={{ color: 'var(--ink)' }}>
                {currentAnchor ? `Update` : `Initialize`} <span className="text-capitalize op-setup-highlight">{selectedFrequency === 'biweekly' ? 'Bi-Weekly' : selectedFrequency}</span> Schedule
              </h4>
              <p className="op-subtitle mx-auto mb-4" style={{ maxWidth: '450px', fontSize: '0.95rem', lineHeight: '1.6' }}>
                {currentAnchor
                  ? 'Modifying the anchor date will recalculate all periods globally. Proceed with caution.'
                  : 'This is a one-time configuration. Provide the very first day of this pay cycle, and the system will automatically calculate all future payroll periods.'}
              </p>

              <div className="d-flex flex-column flex-sm-row justify-content-center gap-3 mx-auto" style={{ maxWidth: '400px' }}>
                <input
                  type="date"
                  className="form-control form-control-lg shadow-sm op-setup-input"
                  value={setupDate}
                  onChange={(e) => setSetupDate(e.target.value)}
                />
                <button
                  className="btn op-btn-primary btn-lg fw-bold px-4 shadow-sm text-nowrap"
                  onClick={handleOneTimeSetup}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Saving</>
                  ) : (
                    currentAnchor ? 'Save Update' : 'Initialize Cycle'
                  )}
                </button>
              </div>

              {currentAnchor && (
                <button className="btn btn-link op-cancel-link mt-4 text-decoration-none small" onClick={() => setSetupDate('')}>
                  Cancel Update
                </button>
              )}
            </div>
          </div>
        </div>

      ) : (

        /* Dynamic Data Grid */
        <div className="card op-card shadow-sm border-0 rounded-3">
          <div className="card-header op-grid-header p-3 p-md-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="badge op-status-pill px-2 py-1 d-flex align-items-center gap-1 rounded-pill">
                  <Icons.FileCheck /> Active
                </span>
                <span className="op-anchor-note small">
                  Dynamically generated from anchor: <strong className="op-anchor-value ms-1">{currentAnchor}</strong>
                </span>
              </div>
              <button
                type="button"
                className="btn btn-sm op-modify-btn shadow-sm fw-medium px-3 w-100 w-md-auto"
                onClick={() => setSetupDate(currentAnchor)}
              >
                <Icons.Edit /> Modify Anchor
              </button>
            </div>
          </div>

          <div className="card-body p-0">

            {/* Phone layout: stacked cards. Five nowrap columns can't shrink to
                phone width, so below the sm breakpoint we drop the table for cards instead. */}
            <div className="d-sm-none op-period-card-list">
              {displayPeriods.map((period) => (
                <div key={period.id} className="op-period-card border-bottom p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="fw-semibold op-period-name small">{period.period_name}</span>
                    <span className="op-periods-mono small fw-bold op-seq">
                      #{String(period.period_number).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="op-card-label">Cycle Start</div>
                      <div className="small op-cell-muted">{period.start_date}</div>
                    </div>
                    <div className="col-6">
                      <div className="op-card-label">Cycle End</div>
                      <div className="small op-cell-muted">{period.end_date}</div>
                    </div>
                    <div className="col-12 mt-1">
                      <div className="op-card-label">Official Pay Date</div>
                      <div className="op-periods-mono fw-bold small op-paydate-value">{period.pay_date}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tablet/desktop layout: real table, sm breakpoint and up */}
            <div className="table-responsive d-none d-sm-block">
              <table className="table table-hover align-middle mb-0 text-nowrap">
                <thead className="op-thead border-bottom" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  <tr>
                    <th className="ps-4 py-3 border-0 text-uppercase">Sequence</th>
                    <th className="py-3 border-0 text-uppercase">Description</th>
                    <th className="py-3 border-0 text-uppercase">Cycle Start</th>
                    <th className="py-3 border-0 text-uppercase">Cycle End</th>
                    <th className="pe-4 py-3 border-0 text-uppercase">Official Pay Date</th>
                  </tr>
                </thead>
                <tbody>
                  {displayPeriods.map((period, index) => (
                    <tr key={period.id} className="border-bottom">
                      <td className="ps-4 py-3 fw-bold op-seq op-periods-mono small">
                        {String(period.period_number).padStart(2, '0')}
                      </td>
                      <td className="py-3">
                        <span className="fw-semibold op-period-name small">{period.period_name}</span>
                      </td>
                      <td className="py-3 op-cell-muted small">{period.start_date}</td>
                      <td className="py-3 op-cell-muted small">{period.end_date}</td>
                      <td className="pe-4 py-3 op-paydate-cell">
                        <span className="fw-bold op-paydate-value op-periods-mono small">{period.pay_date}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollPeriods;